/**
 * Series keys for multi-metric × multi-Z charts use the form `Y-Z1-Z2-...`
 * where Y is the metric display label and Z values are joined with hyphens.
 */

export function buildYZSeriesKey(metricLabel: string, zValues?: string[] | null): string {
    if (!zValues?.length) return metricLabel;
    return `${metricLabel}-${zValues.join('-')}`;
}

/**
 * Given a series key and the set of metric labels, returns which metric label the series belongs to
 * (longest match wins so labels that share a prefix resolve correctly).
 */
export function matchLongestMetricLabel(seriesKey: string, metricLabels: string[]): string | undefined {
    const sorted = [...metricLabels].filter((s) => String(s).length > 0).sort((a, b) => b.length - a.length);
    for (const m of sorted) {
        if (seriesKey === m || seriesKey.startsWith(`${m}-`)) return m;
    }
    return undefined;
}

type MetricColorConfig = { label?: string; dataKey: string; color: string };

/**
 * Picks the color for a `Y-Z1-Z2...` series: config color when the series is exactly one metric, otherwise a palette slot by series order.
 */
export function resolveMetricSeriesColor(
    seriesKey: string,
    orderedUniqueSeries: string[],
    metricConfigs: MetricColorConfig[],
    fallbackPalette: string[],
): string {
    const idx = orderedUniqueSeries.indexOf(seriesKey);
    const labels = metricConfigs.map((c) => c.label || c.dataKey);
    const baseMetric = matchLongestMetricLabel(seriesKey, labels);
    const baseConfig = baseMetric ? metricConfigs.find((c) => (c.label || c.dataKey) === baseMetric) : undefined;
    if (baseConfig && seriesKey === baseMetric) return baseConfig.color;
    const i = idx >= 0 ? idx : 0;
    return fallbackPalette[i % fallbackPalette.length];
}
